import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import { css } from "styled-components"
import { IContentfulResponsiveImage } from "../../design-system/atoms/breakpoint-image"
import { Spacer } from "../../design-system/atoms/layout-primitives"
import { T, Text } from "../../design-system/atoms/text"
import {
  ITestimonialCard,
  TESTIMONIAL_TYPENAME,
} from "../../design-system/molecules/testimonial-card"
import TestimonialCardSlider from "../../design-system/organisms/testimonial-card-slider"
import {
  IFluidImageQuery,
  IFluidImageQueryArray,
} from "../../design-system/types/imageQuery.types"
import Grid from "../shared/grid"

interface ITestimonialsStaticQuery {
  simonLogo: IFluidImageQuery
  mohawkLogo: IFluidImageQuery
  miracleLogo: IFluidImageQuery
  sonaeLogo: IFluidImageQuery
  mallPlazaLogo: IFluidImageQuery
  macerichLogo: IFluidImageQuery
  hudsonLogo: IFluidImageQuery
  pointrLogo: IFluidImageQuery
  marinaLogo: IFluidImageQuery
  intersectionLogo: IFluidImageQuery
  mobile: IFluidImageQueryArray
  tablet: IFluidImageQueryArray
  desktop: IFluidImageQueryArray
}

export default function HomepageTestimonials() {
  const {
    simonLogo,
    mohawkLogo,
    miracleLogo,
    sonaeLogo,
    mallPlazaLogo,
    macerichLogo,
    hudsonLogo,
    pointrLogo,
    marinaLogo,
    intersectionLogo,
    mobile,
    tablet,
    desktop,
  } = useStaticQuery<ITestimonialsStaticQuery>(graphql`
    query {
      simonLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/simon.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mohawkLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/mohawk college.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      miracleLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/miracle mile shops.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      sonaeLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/sonae sierra.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mallPlazaLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/mallplaza.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      macerichLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/macerich.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      hudsonLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/hudson yards.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      pointrLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/pointr.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      marinaLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/marina mall.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      intersectionLogo: file(
        relativePath: { eq: "0_HOMEPAGE/logo selector/intersection.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 200, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobile: allFile(
        filter: {
          relativeDirectory: { eq: "0_HOMEPAGE/testimonial maps/mobile" }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 600, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
      tablet: allFile(
        filter: {
          relativeDirectory: { eq: "0_HOMEPAGE/testimonial maps/tablet" }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 1024, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
      desktop: allFile(
        filter: {
          relativeDirectory: { eq: "0_HOMEPAGE/testimonial maps/desktop" }
        }
        sort: { fields: base }
      ) {
        edges {
          node {
            id
            base
            childImageSharp {
              fluid(maxWidth: 600, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
  `)

  const getMapImage = (index): IContentfulResponsiveImage => {
    // get the map image based on breakpoint & index

    return {
      __typename: "ContentfulResponsiveImage",
      mobile: mobile.edges[index].node.childImageSharp,
      tablet: tablet.edges[index].node.childImageSharp,
      desktop: desktop.edges[index].node.childImageSharp,
    }
  }

  // Testimonial content to be mapped to cards
  const testimonialDetails: ITestimonialCard[] = [
    {
      // Simon
      image: getMapImage(0),
      logos: [simonLogo.childImageSharp],
      author: "Patrick Flanagan",
      jobTitle: "SVP, Digital Strategy, Development & Operations",
      quote:
        "“The easy-to-use platform allows our team to make immediate updates to maps based on store changes, seasonal promotions, and special events. We’re continually investing in technologies such as Mappedin to ensure our centers deliver a digitally driven shopping experience.”",
    },
    {
      // Mohawk College
      image: getMapImage(1),
      logos: [mohawkLogo.childImageSharp],
      author: "Samara Young",
      jobTitle: "Manager, Student Lifecycle Communications",
      quote:
        "“Mohawk College was looking for an interactive digital mapping solution to feature on our website so that students can search for classrooms, services, and more at two of our campuses. Mappedin’s solution provided this, along with a powerful Map Editor platform that allows us to easily update our maps as locations change and grow.”",
    },
    {
      // Miracle Mile Shops
      image: getMapImage(2),
      logos: [miracleLogo.childImageSharp],
      author: "Wendy Albert",
      jobTitle: "Senior Director of Marketing",
      quote:
        "“Mappedin’s responsive mapping solution with blue dot wayfinding allows us to provide interactive and intuitive wayfinding to visitors of Miracle Mile Shops. By integrating the maps with a conversational AI platform, it is simple for tourists and other guests to search, discover, and navigate our venue directly from our website.”",
    },
    {
      // Sonae Sierra
      image: getMapImage(3),
      logos: [sonaeLogo.childImageSharp],
      author: "Ana Fé",
      jobTitle: "Digital Marketing Coordinator",
      quote:
        "“We’re continually looking for state of the art technologies to increase shopper engagement. Mappedin’s web solution adds a consistent means for shoppers to navigate our centres, providing an interactive and immersive experience. The Mappedin technology has highly contributed to improving our customers' digital experience.”",
    },
    {
      // Mallplaza
      image: getMapImage(4),
      logos: [mallPlazaLogo.childImageSharp],
      author: "Violena Meersohn",
      jobTitle: "Agility and Digital Products Deputy Manager",
      quote:
        "“Mappedin’s web solution offers a flexible and interactive mapping solution for visitors to our shopping centres and the Map Editor makes it easy to manage all of the maps in the Mallplaza portfolio. We really value the customer experience that Mappedin delivered to our team from start to finish. We’re looking forward to continuing our work together.”",
    },
    {
      // Macerich
      image: getMapImage(5),
      logos: [macerichLogo.childImageSharp],
      author: "Kurt Ivey",
      jobTitle: "Vice President of Marketing",
      quote:
        "“With up to 16 two-sided digital directories per property, we're pleased to deliver unprecedented opportunities for stores and brand partners to share their messages with shoppers at Macerich's market-leading malls. These responsive, easy-to-use directories will be a magnet for shoppers as well as a great new tool for our retail partners.”",
    },
    {
      // Intersection | Hudson Yards
      image: getMapImage(6),
      logos: [hudsonLogo.childImageSharp, intersectionLogo.childImageSharp],
      author: "Rachel Bell",
      jobTitle: "VP of Client Development",
      quote:
        "“Mappedin’s enterprise tools were invaluable in allowing us to present all of the maps necessary to navigate this complex facility. Through their maps and CMS, we were able to create an unparalleled kiosk experience for the visitors of Hudson Yards.”",
    },
    {
      // Pointr
      image: getMapImage(7),
      logos: [pointrLogo.childImageSharp],
      author: "Trevayne O'Brien",
      jobTitle: "Head of Partnerships",
      quote:
        "“Mappedin has provided a well built indoor mapping platform for Pointr to layer its Deep Location Technology on top of. Together, we’re able to provide and end-to-end solution for indoor mapping, wayfinding, and location analytics.”",
    },
    {
      // Marina Mall
      image: getMapImage(8),
      logos: [marinaLogo.childImageSharp],
      author: "Marina Mall Abu Dhabi Management",
      jobTitle: "",
      quote:
        "“Mappedin was our choice mapping provider because of their ability to deliver modern interactive maps and an easy-to-use Map Editor. Their platform enabled us to make easy and seamless edits within the shopping mall, and provide a digital experience to our visitors, all while increasing online traffic on our website.”",
    },
  ].map((item, index) => ({
    ...item,
    __typename: TESTIMONIAL_TYPENAME,
    contentful_id: `${index}`,
  }))
  return (
    <>
      <Grid>
        <Spacer height={{ sm: "48px", lg: "96px" }} />
        <Text
          variants={[
            css`
              ${T.sm.h2`400`};
              color: var(--slate);
              text-align: center;
              grid-column: 1/-1;
              & > span {
                font-weight: 500;
                color: var(--burnt-orange);
              }
            `,
            css`
              ${T.md.h2`400`};
            `,
            css`
              ${T.lg.h2`400`};
              grid-column: 3/-3;
            `,
          ]}
        >
          People have <span>great things to say</span>
        </Text>
        <Spacer height={{ sm: "24px" }} />
        <Text.Body
          variants={[
            css`
              text-align: center;
              font-size: 18px;
              grid-column: 1/-1;
              color: var(--body-text-grey);
            `,
            css`
              ${T.md.body`400`};
              grid-column: 2/-2;
            `,
            css`
              ${T.lg.body`400`};
              grid-column: 3/-3;
            `,
          ]}
        >
          We put our customers and partners first. This has led us to digitize
          more premium indoor maps than any other provider. Our emphasis on
          customer support and success ensures that we deliver continuous value
          and exceed expectations.
        </Text.Body>
        <Spacer height={{ sm: "48px", lg: "96px" }} />
      </Grid>
      {/* Testimonial slider has its own grid wrapper */}
      <TestimonialCardSlider testimonials={testimonialDetails} />
      <Grid>
        <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
      </Grid>
    </>
  )
}
